/** Shared Resource / Discipline legend colors for EVM histogram + Staffing. */

export const DISC_COLORS = {
  piping: { field: '#2563eb', shop: '#0ea5e9' },
  mechanical: { field: '#059669', shop: '#34d399' },
  electrical: { field: '#ca8a04', shop: '#eab308' },
  civil: { field: '#78716c', shop: '#a8a29e' },
  structural: { field: '#d97706', shop: '#f59e0b' },
  hvac: { field: '#0891b2', shop: '#22d3ee' },
  ducts: { field: '#db2777', shop: '#f472b6' },
  indirect: { field: '#64748b', shop: '#94a3b8' },
  boilermaker: { field: '#ea580c', shop: '#fb923c' },
  vessels: { field: '#7c3aed', shop: '#a78bfa' },
  unknown: { field: '#94a3b8', shop: '#cbd5e1' }
};

export const DISC_LABELS = {
  piping: 'Piping',
  mechanical: 'Mechanical',
  electrical: 'Electrical',
  civil: 'Civil',
  structural: 'Structural',
  hvac: 'HVAC',
  ducts: 'Ducts',
  indirect: 'Indirect',
  boilermaker: 'Boilermaker',
  vessels: 'Vessels',
  unknown: 'Other'
};

/** Colour for a live P6 discipline id (UDF text, lowercased) or catalog slug. */
export function colorForDisciplineId(id) {
  const s = String(id || '').toLowerCase().trim();
  if (!s || s === 'unknown' || s === '__none__') return DISC_COLORS.unknown.field;
  if (DISC_COLORS[s]) return DISC_COLORS[s].field;
  const keys = Object.keys(DISC_COLORS).filter((k) => k !== 'unknown');
  for (const k of keys) {
    if (s.includes(k) || k.includes(s)) return DISC_COLORS[k].field;
  }
  if (/pip(e|ing)/.test(s) && /fab/.test(s)) return DISC_COLORS.piping.shop;
  if (/pip(e|ing)/.test(s)) return DISC_COLORS.piping.field;
  if (/electric/.test(s)) return DISC_COLORS.electrical.field;
  if (/mechanic|millwright/.test(s)) return DISC_COLORS.mechanical.field;
  if (/steel|struct/.test(s)) return DISC_COLORS.structural.field;
  if (/civil/.test(s)) return DISC_COLORS.civil.field;
  if (/hvac/.test(s)) return DISC_COLORS.hvac.field;
  if (/duct/.test(s)) return DISC_COLORS.ducts.field;
  if (/boiler|tank|vessel/.test(s)) return DISC_COLORS.boilermaker.field;
  if (/indirect/.test(s)) return DISC_COLORS.indirect.field;
  return RESOURCE_STACK_PALETTE[hashIndex(s, RESOURCE_STACK_PALETTE.length)];
}

export function resourceColor(r = {}) {
  const key = String(r.discipline || '').toLowerCase();
  if (DISC_COLORS[key]) {
    const pair = DISC_COLORS[key];
    return r.shopOrField === 'shop' ? pair.shop : pair.field;
  }
  return colorForDisciplineId(r.discipline);
}

/** Distinct hues when two resources share a discipline colour (e.g. SF + TF → unknown grey). */
const RESOURCE_STACK_PALETTE = [
  '#2563eb', '#d97706', '#059669', '#7c3aed', '#db2777',
  '#0891b2', '#ea580c', '#ca8a04', '#dc2626', '#0f766e',
  '#4f46e5', '#be185d'
];

function hashIndex(str, mod) {
  let h = 0;
  const s = String(str || '');
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h) + s.charCodeAt(i);
  return Math.abs(h) % mod;
}

export function resourceStackColor(rec = {}) {
  const disc = String(rec.discipline || 'unknown').toLowerCase();
  if (disc && disc !== 'unknown') return resourceColor(rec);
  const key = rec.resourceId || rec.resourceKey || rec.label || 'other';
  return RESOURCE_STACK_PALETTE[hashIndex(key, RESOURCE_STACK_PALETTE.length)];
}

export function assignDistinctStackColors(items) {
  const used = new Set();
  return (items || []).map((item) => {
    let { color } = item;
    if (used.has(color)) {
      const next = RESOURCE_STACK_PALETTE.find((c) => !used.has(c));
      if (next) color = next;
    }
    used.add(color);
    return color === item.color ? item : { ...item, color };
  });
}

/** Hidden LabelList helpers (_budgetTotal / _remainTotal) must never appear in the legend. */
export function isInternalChartSeries(name) {
  return String(name || '').startsWith('_');
}

/**
 * Legend entries only for series that actually put something on the chart —
 * Estimation with no earned or spent yet must not list Per. Earned / Cum. Spent.
 */
export function legendItemsWithData(items, rows) {
  const list = Array.isArray(rows) ? rows : [];
  return (items || []).filter((item) => {
    const key = item?.dataKey;
    if (!key) return true;
    return list.some((row) => Math.abs(Number(row?.[key]) || 0) > 1e-9);
  });
}

export function disciplineColor(disc) {
  return colorForDisciplineId(disc);
}

export function disciplineLabel(disc, rec) {
  if (rec?.disciplineLabel) return rec.disciplineLabel;
  const key = String(disc || 'unknown').toLowerCase();
  if (key === '__none__') return '(no value)';
  return DISC_LABELS[key] || String(disc || 'Other');
}

export function shortResourceLabel(r = {}) {
  const key = String(r.resourceKey || r.resourceId || '');
  const tail = key.includes('-') ? key.split('-').filter(Boolean).slice(-2).join('-') : key;
  return (tail || r.label || r.resourceId || '?').replace(/^-/, '');
}

export function isExtraLabel(r = {}) {
  if (r.isExtra) return true;
  const id = String(r.resourceId || r.resourceKey || r.label || '');
  const name = String(r.resourceName || r.rsrc_name || '');
  if (/(?:^|[-_\/])ex(?:[-_\/]|$)/i.test(id)) return true;
  return /extra|additional\s*scope/i.test(name);
}

/** EI · field vs EI · extra · fab — P6 keeps baseline and extra as two IDs. */
export function resourceFilterLabel(r = {}) {
  const bits = [shortResourceLabel(r)];
  if (isExtraLabel(r)) bits.push('extra');
  if (r.shopOrField === 'shop') bits.push('fab');
  else if (r.shopOrField === 'field') bits.push('field');
  return bits.join(' · ');
}

/** Bar stack by any P6 column (Resource ID/Name, Activity codes, UDFs…). */
export function isP6LegendBy(v) {
  return String(v || '').startsWith('p6:');
}

/** Spent / employee columns (Time Tracker slicers and Accounting op-code fields). */
export function isColumnLegend(v) {
  const s = String(v || '');
  return s.startsWith('acct:') || s.startsWith('tt:');
}

/** stack_by value the backend expects for a p6:<field> legend choice. */
export function p6StackByParam(legendBy) {
  return isP6LegendBy(legendBy) ? String(legendBy).slice(3) : '';
}

/**
 * Resource ID / Name already live on every byResource row. Switching those
 * legends must not wait on a P6 rebuild (that is what froze Refresh on 0163).
 */
const CLIENT_P6_STACKS = new Set(['rsrc_id', 'rsrc_name']);

export function isClientP6Legend(v) {
  return CLIENT_P6_STACKS.has(p6StackByParam(v));
}

/** Activity codes / names / UDFs still need an assignment-level split. */
export function needsBackendLegend(v) {
  return isP6LegendBy(v) && !isClientP6Legend(v);
}

export function projectLegendId(rec = {}) {
  if (rec.projId != null && rec.projId !== '') return String(rec.projId);
  if (rec.projLabel) return String(rec.projLabel);
  if (rec.projShortName) return String(rec.projShortName);
  return 'unknown';
}

export function projectLegendLabel(rec = {}) {
  return rec.projLabel || rec.projShortName || rec.projName || projectLegendId(rec);
}

export function stackKey(rec, legendBy) {
  if (legendBy === 'discipline') return `d:${rec.discipline || 'unknown'}`;
  if (legendBy === 'project') return `j:${projectLegendId(rec)}`;
  if (legendBy === 'p6:rsrc_id') return `p:${rec.resourceId || rec.legendId || 'other'}`;
  if (legendBy === 'p6:rsrc_name') return `p:${rec.resourceName || rec.legendLabel || rec.resourceId || 'other'}`;
  if (isP6LegendBy(legendBy) || isColumnLegend(legendBy)) return `p:${rec.legendId || rec.legendLabel || 'other'}`;
  return `r:${rec.resourceId || rec.resourceKey || 'other'}`;
}

export function stackMeta(rec, legendBy) {
  if (legendBy === 'discipline') {
    const id = rec.discipline || 'unknown';
    return { id: `d:${id}`, label: disciplineLabel(id, rec), color: disciplineColor(id) };
  }
  if (legendBy === 'project') {
    const id = projectLegendId(rec);
    return {
      id: `j:${id}`,
      label: projectLegendLabel(rec),
      color: resourceStackColor({ ...rec, resourceId: id, discipline: 'unknown' })
    };
  }
  if (legendBy === 'p6:rsrc_id') {
    const id = rec.resourceId || rec.legendId || rec.resourceKey || 'other';
    return { id: `p:${id}`, label: id, color: resourceStackColor(rec) };
  }
  if (legendBy === 'p6:rsrc_name') {
    const name = rec.resourceName || rec.legendLabel || rec.resourceId || 'other';
    return { id: `p:${name}`, label: name, color: resourceStackColor({ ...rec, resourceId: name }) };
  }
  if (isP6LegendBy(legendBy) || isColumnLegend(legendBy)) {
    const id = rec.legendId || rec.legendLabel || 'other';
    return {
      id: `p:${id}`,
      label: rec.legendLabel || rec.legendId || '(no value)',
      color: resourceStackColor({ resourceId: id, discipline: rec.discipline || 'unknown' })
    };
  }
  return {
    id: `r:${rec.resourceId || rec.resourceKey || 'other'}`,
    label: resourceFilterLabel(rec),
    color: resourceStackColor(rec)
  };
}

export const LEGEND_BY_KEY = 'evm-legend-by-v3';
/** P6 Resource ID — the Histogram Legend default. */
export const DEFAULT_LEGEND_BY = 'p6:rsrc_id';
/** Bump when the product default changes so old One-colour job prefs migrate once. */
export const LEGEND_DEFAULT_REV = 3;

/** Prefix so Budget stack keys never collide with Remaining (`r:` / `d:`). */
export function budgetStackKey(id) {
  return `b:${id}`;
}

/** Earned stack keys — same legend values, separate series from Remaining. */
export function earnedStackKey(id) {
  return `e:${id}`;
}

/** Spent stack keys. */
export function spentStackKey(id) {
  return `a:${id}`;
}

/**
 * Group filtered resources for stacked histogram bars.
 * `legendBy === 'total'` returns [] so the chart draws the single-colour series.
 */
/** True when a live curve can be split into legend stacks. */
export function curveHasResources(curve) {
  return Boolean(curve && Object.keys(curve.byResource || {}).length);
}

/**
 * Budget stack source. Many estimates have no `-P` proposal XER, so proposal
 * is empty even though Current target / remaining already has the trades.
 */
export function pickBudgetStackCurve({ proposal, weeklyTarget, remaining } = {}) {
  if (curveHasResources(proposal)) return proposal;
  if (curveHasResources(weeklyTarget)) return weeklyTarget;
  if (curveHasResources(remaining)) return remaining;
  return proposal || weeklyTarget || remaining || null;
}

export function collectStackItems(resources, legendBy) {
  if (legendBy === 'total' || !Array.isArray(resources) || !resources.length) return [];
  const map = new Map();
  resources.forEach((r) => {
    if (!r) return;
    const meta = stackMeta(r, legendBy);
    if (!map.has(meta.id)) map.set(meta.id, { ...meta, resources: [] });
    map.get(meta.id).resources.push(r);
  });
  const items = [...map.values()].sort((a, b) => String(a.label).localeCompare(String(b.label)));
  return (legendBy === 'resource' || legendBy === 'project' || isP6LegendBy(legendBy) || isColumnLegend(legendBy))
    ? assignDistinctStackColors(items)
    : items;
}

export function loadLegendBy() {
  try {
    const v = localStorage.getItem(LEGEND_BY_KEY);
    if (v === 'resource' || v === 'discipline' || v === 'total' || v === 'project' || isP6LegendBy(v) || isColumnLegend(v)) return v;
  } catch { /* ignore */ }
  return DEFAULT_LEGEND_BY;
}

/** Job prefs win after the user picks a legend; 'total' from the old default becomes Resource ID once. */
export function resolveJobLegendBy(prefs) {
  const saved = prefs?.legendBy;
  const rev = Number(prefs?.legendDefaultRev) || 0;
  if (rev >= LEGEND_DEFAULT_REV && saved) return saved;
  if (!saved || saved === 'total') return DEFAULT_LEGEND_BY;
  return saved;
}

export function saveLegendBy(v) {
  try { localStorage.setItem(LEGEND_BY_KEY, v); } catch { /* ignore */ }
}

export const LEGEND_COLORS_KEY = 'evm-legend-colors-v1';

export function legendColorsStorageKey(jobNumber) {
  return `${LEGEND_COLORS_KEY}:${jobNumber || 'default'}`;
}

/** `#rgb` / `#rrggbb` only — otherwise the fallback. */
export function normalizeLegendHex(color, fallback) {
  const s = String(color || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return fallback;
}

export const LEGEND_PATTERNS = [
  { id: 'solid', label: 'Solid' },
  { id: 'hash', label: 'Hash' },
  { id: 'hashFwd', label: 'Hash /' },
  { id: 'cross', label: 'Cross' },
  { id: 'horiz', label: 'Lines' },
  { id: 'dots', label: 'Dots' }
];
export const LEGEND_PATTERN_IDS = LEGEND_PATTERNS.map((p) => p.id);

export function legendPaintEntry(overrides, key) {
  const raw = overrides?.[key];
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return { color: raw.color, pattern: raw.pattern };
  }
  if (typeof raw === 'string') return { color: raw, pattern: 'solid' };
  return {};
}

export function legendColor(overrides, key, fallback) {
  return normalizeLegendHex(legendPaintEntry(overrides, key).color, fallback);
}

export function legendPattern(overrides, key, fallback = 'solid') {
  const p = legendPaintEntry(overrides, key).pattern;
  return LEGEND_PATTERN_IDS.includes(p) ? p : fallback;
}

export function setLegendPaint(overrides, key, patch = {}) {
  const prev = legendPaintEntry(overrides, key);
  const nextColor = normalizeLegendHex(
    patch.color !== undefined ? patch.color : prev.color,
    prev.color || null
  );
  const requested = patch.pattern !== undefined ? patch.pattern : (prev.pattern || 'solid');
  const nextPattern = LEGEND_PATTERN_IDS.includes(requested) ? requested : 'solid';
  const next = { ...(overrides || {}) };
  if (!nextColor) {
    delete next[key];
    return next;
  }
  if (nextPattern === 'solid') {
    next[key] = nextColor;
    return next;
  }
  next[key] = { color: nextColor, pattern: nextPattern };
  return next;
}

export function hatchInk(hex, fallback = '#0f172a') {
  const h = normalizeLegendHex(hex, fallback);
  const r = parseInt(h.slice(1, 3), 16);
  const g = parseInt(h.slice(3, 5), 16);
  const b = parseInt(h.slice(5, 7), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#0f172a' : 'rgba(255,255,255,0.88)';
}

export function legendPatternFill(hex, pattern) {
  const color = normalizeLegendHex(hex, '#64748b');
  const ink = hatchInk(color);
  switch (pattern) {
    case 'hash':
      return { backgroundImage: `repeating-linear-gradient(-45deg, ${color} 0 4px, ${ink} 4px 5.5px)` };
    case 'hashFwd':
      return { backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 4px, ${ink} 4px 5.5px)` };
    case 'cross':
      return {
        backgroundImage: [
          `repeating-linear-gradient(-45deg, ${color} 0 4px, ${ink} 4px 5.5px)`,
          `repeating-linear-gradient(45deg, transparent 0 4px, ${ink} 4px 5.5px)`
        ].join(', ')
      };
    case 'horiz':
      return { backgroundImage: `repeating-linear-gradient(0deg, ${color} 0 3px, ${ink} 3px 4.5px)` };
    case 'dots':
      return {
        backgroundColor: color,
        backgroundImage: `radial-gradient(${ink} 1.1px, transparent 1.2px)`,
        backgroundSize: '6px 6px'
      };
    default:
      return { background: color };
  }
}

export function hatchLineSpecs(pattern, x, y, w, h, step = 6) {
  const specs = [];
  if (!(w > 0 && h > 0) || !LEGEND_PATTERN_IDS.includes(pattern) || pattern === 'solid' || pattern === 'dots') {
    return specs;
  }
  if (pattern === 'hash' || pattern === 'cross') {
    for (let i = -h; i < w + step; i += step) {
      specs.push({ x1: x + i, y1: y, x2: x + i + h, y2: y + h });
    }
  }
  if (pattern === 'hashFwd' || pattern === 'cross') {
    for (let i = 0; i < w + h + step; i += step) {
      specs.push({ x1: x + i, y1: y, x2: x + i - h, y2: y + h });
    }
  }
  if (pattern === 'horiz') {
    for (let yy = y + step / 2; yy < y + h; yy += step) {
      specs.push({ x1: x, y1: yy, x2: x + w, y2: yy });
    }
  }
  return specs;
}

export function hatchDotSpecs(pattern, x, y, w, h, step = 5) {
  if (pattern !== 'dots' || !(w > 0 && h > 0)) return [];
  const dots = [];
  for (let yy = y + 2.5; yy < y + h; yy += step) {
    for (let xx = x + 2.5; xx < x + w; xx += step) {
      dots.push({ cx: xx, cy: yy });
    }
  }
  return dots;
}

export function loadLegendColors(jobNumber) {
  try {
    const raw = localStorage.getItem(legendColorsStorageKey(jobNumber));
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch { /* ignore */ }
  return {};
}

export function saveLegendColors(jobNumber, map) {
  try {
    localStorage.setItem(legendColorsStorageKey(jobNumber), JSON.stringify(map || {}));
  } catch { /* ignore */ }
}
